<template lang="pug">
.dev-doc--screen
  .row(v-if='webhookEventsList.length > 0') 
    .col-12
      .row
        .mb-3.col-12.col-lg-6.col-xl-5
          .form-group.mb-0.d-flex.align-items-center.justify-content-between.flex-wrap.justify-content-md-start
            .form-control-icon--end-line.flex-grow-1 
              input.form-control.rounded-3(
                :placeholder='$t("placeholder.search_webhook_events")'
                @input='searchWebhookEventsList()'
                autocomplete='off'
                v-model='filters.search'
              )
              icon.icon--blue.font-size-subtitle(name='magnifying-glass') 
            filter-selector.me-auto(
              :filters='mountFilterConfig'
              @update='updateFilter'
            )
        // - filter events by date range 
        .mb-3.col-12.col-lg-6.col-xl-7
          .d-flex.align-items-center.gap-2.w-100
            .form-control-icon.flex-grow-1
              icon.icon--blue(name='calendar')
              date-picker-range-adapter#selectStartDate.date_picker_range_adapter.rounded-3(
                :placeholder='$t("placeholder.search_webhook_date_to")'
                v-model='startDate'
              )
            .form-control-icon.flex-grow-1
              icon.icon--blue(name='calendar')
              date-picker-range-adapter#selectFinishDate.date_picker_range_adapter.rounded-3(
                :min-date='startDate'
                :placeholder='$t("placeholder.search_webhook_date_from")'
                v-model='finishDate'
              )
            button.btn-filter.font-color-blue.rounded-circle(
              :disabled='!startDate || !finishDate'
              @click='fetchWebhookEventsList()'
            )
              icon.icon--blue(name='magnifying-glass')

    .col-12.mt-3
      webhook-events-card-table.mb-5(
        :data='webhookEventsList'
        @listWebhooks='fetchWebhookEventsList'
        @refresh='fetchWebhookEventsList'
        @sort='setSortColumn'
      )
      simple-paginator.mt-3(
        :payload='payload'
        @navigate='fetchWebhookEventsList'
      )
  .row(v-else)
    .col-12
      .text-center.mt-5
        img.img(
          src='/img/ilustrations/empty-applications.svg'
          style='max-height: 260px'
        )
        p.m-auto.mt-4.mb-3(style='max-width: 450px') {{ $t('pages.manageWebhookEvents.noEvents') }}
        button.btn.btn-primary(@click='openManageWebhooksPage') {{ $t('pages.manageWebhooks.title') }}
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
// eslint-disable-next-line max-len
import TableComponent from '@/ui/organisms/Table.vue';
import IconEnviromentClient from '@/ui/atoms/IconEnviromentClient.vue';
import SimplePaginator from '@/ui/organisms/SimplePaginator.vue';
// eslint-disable-next-line max-len
import ManageWebhooksCardTable from '@/modules/account/components/organisms/card-table/ManageWebhooksCardTable.vue';
import WebhooksEventsService from '@/services/WebhooksEventsService';
import WebhookEventsCardTable from '@/modules/account/components/organisms/card-table/WebhookEventsCardTable.vue';
import FilterSelector from '@/ui/organisms/FilterSelector.vue';
import debounce from 'debounce';
import moment from 'moment/moment';
import DatePickerRangeAdapter from '@/ui/molecules/DatePickerRangeAdapter.vue';

export default {
  name: 'ManageWebhookEvents',
  components: {
    WebhookEventsCardTable,
    Icon,
    IconEnviromentClient,
    TableComponent,
    SimplePaginator,
    ManageWebhooksCardTable,
    FilterSelector,
    DatePickerRangeAdapter,
  },
  data() {
    return {
      webhookEventsList: [],
      payload: {},
      filters: {
        event_type: '',
        created_at: '',
        status: '',
        url: '',
        search: '',
        webhook_id: '',
        sort: '-created_at',
      },
      startDate: null,
      finishDate: null,
    };
  },
  created() {
    this.$store.dispatch(
      'page/setTitle',
      this.$t('pages.manageWebhookEvents.title')
    );
    this.$store.dispatch(
      'page/setHead',
      this.$t('pages.manageWebhookEvents.head')
    );
    this.$store.dispatch('page/setClass', 'account developer');
    this.fetchWebhookEventsList();
  },
  computed: {
    mountFilterConfig() {
      return [
        {
          name: 'event_type',
          label: this.$t('pages.webhookEventsCardTable.eventType'),
          selected: null,
          options: [
            {
              label: this.$t('pages.manageWebhookEvents.filters.filterAll'),
              value: '',
            },
            {
              label: 'Envelope Created',
              value: 'envelope.status.draft',
            },
            {
              label: 'Envelope Cancelled',
              value: 'envelope.status.cancelled',
            },
            {
              label: 'Envelope Completed',
              value: 'envelope.status.completed',
            },
            {
              label: 'Envelope Expired',
              value: 'envelope.status.expired',
            },
            {
              label: 'Envelope Template',
              value: 'envelope.status.template',
            },
            {
              label: 'Envelope Halted',
              value: 'envelope.status.halted',
            },
            {
              label: 'Envelope In-Transit',
              value: 'envelope.status.in-transit',
            },
            {
              label: 'Envelope Processing',
              value: 'envelope.status.processing',
            },
          ],
        },
      ];
    },
  },
  watch: {
    startDate(val) {
      if (!val) {
        this.finishDate = null;
        this.fetchWebhookEventsList();
      }
    },
    finishDate(val) {
      if (!val) {
        this.startDate = null;
      }
    },
  },
  methods: {
    getFiltersAndSortParams() {
      return {
        sort: this.filters.sort,
        ...(this.filters.created_at && {
          'filter[created_at]': this.filters.created_at,
        }),
        ...(this.filters.event_type && {
          'filter[event_type]': this.filters.event_type,
        }),
        ...(this.filters.search && { q: this.filters.search }),
      };
    },
    async fetchWebhookEventsList(page = 1) {
      const params = {
        page,
        ...this.getFiltersAndSortParams(),
      };
      const id = this.$route.params.webhookId;
      try {
        this.$loading(true);
        const result =
          this.startDate && this.finishDate
            ? await WebhooksEventsService.fetchEventsByDateInterval(
                id,
                moment(this.startDate).toISOString(),
                moment(this.finishDate).toISOString(),
                params
              )
            : await WebhooksEventsService.listEvents(id, params);
        this.payload = result.data;
        this.webhookEventsList = result.data.data;
      } finally {
        this.$loading(false);
      }
    },
    openManageWebhooksPage() {
      this.$router.push({
        name: 'account.developer.webhooks',
      });
    },
    searchWebhookEventsList: debounce(function searchWebhookEventsList() {
      this.fetchWebhookEventsList();
    }, 300),
    updateFilter(selectedFilters) {
      console.log(selectedFilters);
      Object.keys(selectedFilters).forEach((key) => {
        this.filters[key] = selectedFilters[key].value;
      });
      this.searchWebhookEventsList();
    },
    setSortColumn(column) {
      this.filters.sort = `${column.desc ? '-' : ''}${column.column}`;
      this.searchWebhookEventsList();
    },
  },
};
</script>
<style scoped lang="stylus">
.date_picker_range_adapter {
  background: #013D52;
}

.btn-filter
  padding: 6px 11px
  border: none
  background-color: transparent
  &:hover:not(:disabled)
    background-color: #F0F0F0
  &:disabled
    opacity: 0.5
    cursor: not-allowed

@media screen and (max-width: 768px)
  .table
    tbody
      tr
        position: relative
        display: flex
        flex-direction: row
        flex-wrap: wrap
        padding-left: 0
        td
          padding-left: 13px
          flex: 0 0 80%
          &.action
            position: absolute
            top: 35px
            right: -55px
            padding: 0
</style>
